import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import type { ChatContextValue, ChatThread, ChatMessage } from "./types";

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const [thread, setThread] = useState<ChatThread | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const subscriptionRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // ── Load or create thread on mount ────────────────────────────────────────
  useEffect(() => {
    if (!user || !workspace) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    (async () => {
      // Find existing open thread for this user+workspace
      const { data: existing } = await supabase
        .from("chat_threads" as any)
        .select("*")
        .eq("user_id", user.id)
        .eq("workspace_id", workspace.id)
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (existing) {
        setThread(existing as any);
        await loadMessages((existing as any).id);
      }
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user, workspace]);

  // ── Load messages for a thread ────────────────────────────────────────────
  const loadMessages = async (threadId: string) => {
    const { data } = await supabase
      .from("chat_messages" as any)
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true });
    setMessages((data as any[]) || []);
  };

  // ── Realtime subscription ─────────────────────────────────────────────────
  useEffect(() => {
    if (!thread) return;

    const channel = supabase
      .channel(`chat:${thread.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `thread_id=eq.${thread.id}`,
        },
        (payload: any) => {
          const newMsg = payload.new as ChatMessage;
          setMessages((prev) => {
            // Avoid duplicates (e.g. our own insert arriving via realtime)
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        }
      )
      .subscribe();

    subscriptionRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      subscriptionRef.current = null;
    };
  }, [thread?.id]);

  // ── Ensure a thread exists (create lazily on first message) ───────────────
  const ensureThread = useCallback(async (): Promise<ChatThread> => {
    if (thread) return thread;
    if (!user || !workspace) throw new Error("No user or workspace");

    const { data, error } = await supabase
      .from("chat_threads" as any)
      .insert({
        user_id: user.id,
        workspace_id: workspace.id,
      } as any)
      .select()
      .single();

    if (error || !data) throw new Error("Failed to create chat thread");
    const newThread = data as any as ChatThread;
    setThread(newThread);
    return newThread;
  }, [thread, user, workspace]);

  // ── Send a user message ───────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (body: string) => {
      if (!body.trim()) return;
      setSending(true);
      try {
        const t = await ensureThread();
        const { data, error } = await supabase
          .from("chat_messages" as any)
          .insert({
            thread_id: t.id,
            sender_type: "user",
            body: body.trim(),
          } as any)
          .select()
          .single();

        if (error) throw error;
        // Optimistically add (realtime will dedupe)
        if (data) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === (data as any).id)) return prev;
            return [...prev, data as any];
          });
        }

        // Notify Slack via edge function (fire and forget)
        void notifySlack(t.id, body.trim());
      } finally {
        setSending(false);
      }
    },
    [ensureThread]
  );

  // ── Send a system message (from onboarding, etc.) ─────────────────────────
  const sendSystemMessage = useCallback(
    async (body: string, metadata?: Record<string, unknown>) => {
      const t = await ensureThread();
      const { data } = await supabase
        .from("chat_messages" as any)
        .insert({
          thread_id: t.id,
          sender_type: "system",
          body,
          metadata: metadata || {},
        } as any)
        .select()
        .single();

      if (data) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === (data as any).id)) return prev;
          return [...prev, data as any];
        });
      }
    },
    [ensureThread]
  );

  // ── Slack notification (best-effort) ──────────────────────────────────────
  const notifySlack = async (threadId: string, body: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await supabase.functions.invoke("chat-to-slack", {
        body: { thread_id: threadId, message: body },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
    } catch (err) {
      console.error("Failed to notify Slack:", err);
    }
  };

  return (
    <ChatContext.Provider
      value={{ thread, messages, loading, sending, sendMessage, sendSystemMessage }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside <ChatProvider>");
  return ctx;
}
